import { createApiClient } from "@gosaas/api-client";
import type { RequestParams } from "@gosaas/api-client/generated";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { env } from "@/env";
import {
	deleteAccessToken,
	deleteRefreshToken,
	getAccessToken,
	getRefreshToken,
	setAccessToken,
	setRefreshToken,
} from "@/utils/local-storage";
import { tagRequestId } from "@/utils/query-errors";
import { getSentryTraceHeaders } from "@/utils/sentry-trace-headers";

export type { ApiResponse } from "@gosaas/api-client";
export { ApiError, TypedClientApiError } from "@gosaas/api-client";

let lastRateLimitToastAt = 0;

const { client: apiClient, requestWithHandling } = createApiClient({
	baseUrl: env.VITE_BACKEND_URL,
	storage: {
		getAccessToken: () => getAccessToken(),
		getRefreshToken: () => getRefreshToken(),
		setAccessToken: (token) => {
			flushSync(() => setAccessToken(token));
		},
		setRefreshToken: (token) => {
			flushSync(() => setRefreshToken(token));
		},
		deleteAccessToken: () => deleteAccessToken(),
		deleteRefreshToken: () => deleteRefreshToken(),
	},
	onRateLimit: () => {
		const now = Date.now();
		if (now - lastRateLimitToastAt < 5_000) {
			return;
		}
		lastRateLimitToastAt = now;
		toast.error("Too many requests", {
			description: "You've made too many requests. Please try again later.",
		});
	},
	onAccessDenied: () =>
		toast.error("Access denied", {
			description:
				"You don't have permission for this action. Contact your administrator.",
		}),
	getTraceHeaders: getSentryTraceHeaders,
	onRequestId: tagRequestId,
});

// Token refresh on expired access tokens is automatic for all `apiClient.api.*` calls
// (see @gosaas/api-client). Use `requestWithHandling` when you need unwrapped
// `{ data, code, message }`, rate-limit retries, or permission/rate-limit toasts.

/** Verify signup email; optional `email` query disambiguates duplicate tokens. */
export function authVerifyEmailCreate(token: string, email: string) {
	return apiClient.api.authVerifyEmailCreate(token, {
		query: { email },
	} as RequestParams);
}

export { apiClient, requestWithHandling };
